import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { cn } from "@shared/lib/utils";
import { AgentAvatar } from "@/components/collection/AgentAvatar";
import { GlassPanel } from "@/components/ui/GlassPanel";
import { GlowButton } from "@/components/ui/GlowButton";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import type { MyCardInstance } from "@/lib/api";
import { signInWithTelegram } from "@/lib/auth";
import { finishMeta } from "@/lib/cards";
import { HOME_AGENTS } from "@/lib/homeAgents";
import { serialLabel } from "./ListingTile";
import { formatIp } from "./useMarketplace";

const AGENT_BY_ID = new Map(HOME_AGENTS.map((agent) => [agent.agentId, agent]));

export const MIN_PRICE = 1;
export const MAX_PRICE = 1_000_000;

/** Integer IP in [1, 1,000,000] — null when the raw input isn't a valid price. */
export function parsePrice(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const value = Number(raw.trim());
  if (!Number.isSafeInteger(value) || value < MIN_PRICE || value > MAX_PRICE) return null;
  return value;
}

type InstanceRowProps = {
  instance: MyCardInstance;
  pending: boolean;
  onList: (instance: MyCardInstance, price: number) => void;
  onCancel: (listingId: number) => void;
};

/** One owned card — unlisted rows take a price + List; listed rows show ask + Cancel. */
function InstanceRow({ instance, pending, onList, onCancel }: InstanceRowProps) {
  const [raw, setRaw] = useState("");
  const [armed, setArmed] = useState(false);
  const agent = AGENT_BY_ID.get(instance.agentId);
  const meta = finishMeta(instance.finish);

  // Two-step confirm — same 4s auto-disarm contract as Buy — so a fat-fingered
  // price (e.g. "1" instead of "100") on a rare serial card isn't one click
  // from going live. Editing the price also disarms it.
  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(timer);
  }, [armed]);

  if (!agent) return null;

  const price = parsePrice(raw);
  const serial = serialLabel(instance.serial, meta.supplyCap);

  const handleListClick = () => {
    if (pending || price === null) return;
    if (!armed) {
      setArmed(true);
      return;
    }
    setArmed(false);
    onList(instance, price);
  };

  return (
    <GlassPanel className="flex items-center gap-3 p-3 sm:p-3.5">
      <span className="size-9 shrink-0 overflow-hidden rounded-full border border-line">
        <AgentAvatar agent={agent} className="size-full" />
      </span>

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "truncate font-mono text-[10px] font-medium uppercase leading-snug tracking-[0.08em]",
            meta.accentText,
          )}
        >
          {[agent.name, meta.label].join(" · ")}
        </p>
        <p className="mt-0.5 font-mono text-[10px] font-medium uppercase tracking-[0.08em] tabular-nums text-ink-mute">
          {serial ?? "Open edition"}
          {instance.listed && instance.price != null ? (
            <>
              {" · listed at "}
              <span className="text-ink">{formatIp(instance.price)}</span> IP
            </>
          ) : null}
        </p>
      </div>

      {instance.listed && instance.listingId != null ? (
        <button
          type="button"
          disabled={pending}
          onClick={() => onCancel(instance.listingId!)}
          className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-full border border-line px-3.5 py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.1em] text-ink-dim transition-colors duration-200 hover:border-line-hi hover:text-ink disabled:pointer-events-none disabled:opacity-60"
        >
          {pending ? "Cancelling…" : "Cancel listing"}
        </button>
      ) : (
        <div className="flex shrink-0 flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              placeholder="IP"
              aria-label={`Asking price for ${agent.name} ${meta.label} (IP)`}
              value={raw}
              disabled={pending}
              onChange={(event) => {
                setRaw(event.target.value.replace(/[^\d]/g, ""));
                setArmed(false);
              }}
              className="h-11 w-20 rounded-full border border-line bg-white/4 px-3 text-right font-mono text-[11px] font-medium tabular-nums text-ink outline-none transition-colors duration-200 placeholder:text-ink-mute focus:border-accent/60 disabled:opacity-60 sm:w-24"
            />
            <button
              type="button"
              disabled={pending || price === null}
              onClick={handleListClick}
              aria-label={
                armed && price !== null
                  ? `Confirm listing at ${formatIp(price)} IP`
                  : "List this card for sale"
              }
              className="ty-cta inline-flex min-h-11 items-center justify-center rounded-full px-3.5 py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.1em] disabled:pointer-events-none disabled:opacity-50"
            >
              {pending ? "Listing…" : armed ? "Confirm?" : "List"}
            </button>
          </div>
          {/* Live proceeds preview — the only fee disclosure at the point of pricing. */}
          {price !== null ? (
            <p className="text-right font-mono text-[9px] font-medium uppercase tracking-[0.08em] text-ink-mute">
              Receive <span className="tabular-nums text-ink">{formatIp(price * 0.95)}</span> IP after 5% fee
            </p>
          ) : null}
        </div>
      )}
    </GlassPanel>
  );
}

type SellSectionProps = {
  authenticated: boolean;
  sessionPending: boolean;
  instances: MyCardInstance[] | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  pending: ReadonlySet<string>;
  onList: (instance: MyCardInstance, price: number) => void;
  onCancel: (listingId: number) => void;
};

/** Rows shown before the "Show all" expander — keeps Sell from dwarfing Listings above it. */
const INITIAL_VISIBLE_INSTANCES = 8;

/** 02 — Sell: list any card you own at a fixed integer IP price (1..1,000,000). */
export function SellSection({
  authenticated,
  sessionPending,
  instances,
  loading,
  error,
  onRetry,
  pending,
  onList,
  onCancel,
}: SellSectionProps) {
  const [showAll, setShowAll] = useState(false);

  const handleSignIn = useCallback(() => {
    void signInWithTelegram("/marketplace").catch((err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Telegram sign-in failed");
    });
  }, []);

  const busy = sessionPending || loading;
  const visibleInstances =
    instances && !showAll ? instances.slice(0, INITIAL_VISIBLE_INSTANCES) : instances;

  return (
    <section id="sell" className="scroll-mt-24 space-y-5 sm:space-y-6">
      <SectionHeader
        index="02"
        label="Sell"
        title="Sell from your collection"
        hint={`Set an integer IP price (${formatIp(MIN_PRICE)}–${formatIp(MAX_PRICE)}) — your card goes live instantly.`}
      />

      {busy ? (
        <div className="grid gap-3 sm:grid-cols-2" aria-hidden>
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-2xl" />
          ))}
        </div>
      ) : !authenticated ? (
        <GlassPanel className="flex flex-col items-center gap-2.5 px-4 py-4 text-center sm:flex-row sm:justify-between sm:text-left">
          <p className="text-xs text-ink-dim">
            Sign in to list your serial-numbered cards on the market.
          </p>
          <GlowButton onClick={handleSignIn} className="shrink-0">
            Sign in with Telegram
          </GlowButton>
        </GlassPanel>
      ) : error ? (
        <p className="flex items-center gap-3 border-t border-line pt-3 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink-mute">
          Couldn't load your cards — {error}
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex min-h-11 items-center rounded-full border border-line px-3 text-accent transition-colors duration-200 hover:border-accent/60"
          >
            Retry
          </button>
        </p>
      ) : !instances || instances.length === 0 ? (
        <p className="border-t border-line py-10 text-center text-xs text-ink-dim">
          No sellable cards yet — pull serial-numbered finishes from Draw first.
        </p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            {(visibleInstances ?? []).map((instance) => (
              <InstanceRow
                key={instance.instanceId}
                instance={instance}
                pending={
                  pending.has(`instance:${instance.instanceId}`) ||
                  (instance.listingId != null && pending.has(`listing:${instance.listingId}`))
                }
                onList={onList}
                onCancel={onCancel}
              />
            ))}
          </div>
          {!showAll && instances.length > INITIAL_VISIBLE_INSTANCES ? (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="rounded-full border border-line px-5 py-2 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-ink-mute transition-colors duration-200 hover:border-accent/60 hover:text-ink"
              >
                Show all {instances.length} cards
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
